import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';
import classnames from 'classnames';
import ConfigureAPIStage from 'js/components/data_sources/add_ds_form/ConfigureAPIStage';
import { ReactComponent as AddIcon } from 'new/components/icons/fi-rr-plus.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import { StepPaginationControl } from './StepPaginationControl';
import { StepUrlComposer } from './StepUrlComposer';
import { ResponsePanel } from './ResponsePanel';
import { RequestBodyEditor } from './RequestBodyEditor';
import { ResponsePreviewPanel } from './ResponsePreviewPanel';
import { LookupsRow } from './LookupsRow';
import classes from './RestApiStepList.module.scss';
import { DATA_MAP_TYPE } from './iterationFields';

interface FieldOption {
  label: string;
  value: string;
}

const FALLBACK_OPTIONS: Record<string, FieldOption[]> = {
  method: [
    { label: 'GET', value: 'GET' },
    { label: 'POST', value: 'POST' },
    { label: 'PUT', value: 'PUT' },
  ],
  'date.format': [
    { label: 'yyyy-MM-dd', value: 'yyyy-MM-dd' },
    { label: 'MM/dd', value: 'MM/dd' },
    { label: 'yyyy', value: 'yyyy' },
    { label: 'HH:mm', value: 'HH:mm' },
    { label: "yyyy-MM-dd'T'HH:mm:ss'Z'", value: "yyyy-MM-dd'T'HH:mm:ss'Z'" },
  ],
  'date.time.unit': [
    { label: 'Year', value: 'yyyy' },
    { label: 'Month', value: 'MM' },
    { label: 'Day', value: 'dd' },
    { label: 'Hour', value: 'HH' },
    { label: 'Minute', value: 'mm' },
    { label: 'Second', value: 'ss' },
  ],
};

/** Options for a field from the backend defs (paging.sourceConfigById.rest), with a safe fallback. */
const getFieldOptions = (paging: any, fieldName: string): FieldOption[] => {
  const fields = paging?.sourceConfigById?.rest;
  const field = Array.isArray(fields) ? fields.find((f: any) => f?.name === fieldName) : null;
  const opts = field?.options;
  if (Array.isArray(opts) && opts.length) {
    return opts.map((o: any) =>
      typeof o === 'string'
        ? { label: o, value: o }
        : { label: o.label ?? o.value, value: o.value },
    );
  }
  return FALLBACK_OPTIONS[fieldName] ?? [];
};

/**
 * Redesigned Advanced-mode step list for the REST API source config screen.
 *
 * USABILITY-ONLY: this renders the redesigned step-card chrome + chaining
 * visualization from the prototype, but delegates every field input to the
 * proven legacy `ConfigureAPIStage`. It is wired to the EXACT same props and
 * `onAPIConfigChanged(name, value, stageIndex)` contract that the legacy
 * `ConfigureAPI` passes — so the payload it produces is byte-identical.
 *
 * Rendered only when the redesign is enabled via the UI_ENABLE_REST_API_SOURCE_REDESIGN
 * GrowthBook flag (gated in ConfigureAPI).
 */

type Iteration = Record<string, any>;

export interface RestApiStepListProps {
  apiConfig: { 'rest.iterations': Iteration[]; [k: string]: any };
  apiURLMacros: any;
  macrosUpdateTime: number | null;
  apiResponseByStage: any;
  parentDataSet: any;
  parentDataSetSamples: any;
  editedSamples: any;
  /** credential key→value map to resolve {data_credential["…"]} in the sample URL */
  credentialData?: Record<string, any> | null;
  isLoadingProbeSample: boolean;
  isLoadingDataMaps: boolean;
  dataPathValidationResultByStage: any;
  selectedConnection: string;
  paging: any;
  dataMapSamples: any;
  dataMaps: any;
  isAPIServerTarget?: boolean;
  validateDataPath: (...args: any[]) => void;
  setFormError: (...args: any[]) => void;
  loadDataMaps: (...args: any[]) => void;
  onCallAPI: (stageIndex: number) => void;
  onAPIConfigChanged: (name: string, value: any, stageIndex?: number | null, data?: any) => void;
  onGetParameterizedURLs: (stageIndex: number) => any;
  onAddStep: (cb?: () => void) => void;
  onRemoveStep: (stageIndex: number) => void;
  /** rendered inside the left (steps) column below the steps — e.g. Schedule
   *  Ingestion / Select Runtime — so they sit beside the response preview. */
  belowSteps?: React.ReactNode;
}

const getNonDataMapIterations = (iterations: Iteration[] = []) =>
  iterations.filter((it) => it['iteration.type'] !== DATA_MAP_TYPE);

export const RestApiStepList: React.FC<RestApiStepListProps> = (props) => {
  const {
    apiConfig,
    apiURLMacros,
    macrosUpdateTime,
    apiResponseByStage,
    parentDataSet,
    parentDataSetSamples,
    editedSamples,
    credentialData,
    isLoadingProbeSample,
    isLoadingDataMaps,
    dataPathValidationResultByStage,
    selectedConnection,
    paging,
    dataMapSamples,
    dataMaps,
    isAPIServerTarget,
    validateDataPath,
    setFormError,
    loadDataMaps,
    onCallAPI,
    onAPIConfigChanged,
    onGetParameterizedURLs,
    onAddStep,
    onRemoveStep,
    belowSteps,
  } = props;

  const iterations = getNonDataMapIterations(apiConfig['rest.iterations']);
  const methodOptions = useMemo(() => getFieldOptions(paging, 'method'), [paging]);
  const dateFormatOptions = useMemo(() => getFieldOptions(paging, 'date.format'), [paging]);
  const timeUnitOptions = useMemo(() => getFieldOptions(paging, 'date.time.unit'), [paging]);
  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  // Active tab of the global Response Preview; mirrors focus from the in-card
  // Data Path / Metadata path fields.
  const [previewTab, setPreviewTab] = useState<'raw' | 'data' | 'metadata' | 'headers' | 'errors'>(
    'data',
  );

  const toggle = (index: number) => setExpandedIndex((cur) => (cur === index ? -1 : index));

  // The global preview shows the active (expanded) step's response.
  const activeIndex = expandedIndex >= 0 ? expandedIndex : 0;

  // Surface failures: when the active step's response is an error (HTTP >= 400),
  // jump the preview to the Errors tab.
  const activeHasError = Number(apiResponseByStage?.[activeIndex]?.apiResponseCode) >= 400;
  useEffect(() => {
    if (activeHasError) setPreviewTab('errors');
  }, [activeHasError, activeIndex]);

  return (
    <div className={classes.advancedLayout} data-testid="rest-redesign-advanced">
      <div className={classes.stepsColumn}>
        <LookupsRow
          dataMapIds={apiConfig.dataMapIds}
          dataMaps={dataMaps}
          iterations={iterations}
          onAPIConfigChanged={onAPIConfigChanged}
          setFormError={setFormError}
        />
        <div className={classes.stepList} data-testid="rest-redesign-step-list">
          {iterations.map((iteration, index) => {
            const expanded = expandedIndex === index;
            const paginated = !!iteration?.isPaginated;

            return (
              <div
                key={`${iteration?.key ?? index}`}
                id={`rest-api-step-${index}`}
                className={classes.stepBlock}
              >
                {index > 0 && (
                  <div className={classes.chainConnector}>
                    <span className={classes.chainArrow}>
                      <Expand className={classes.chainArrowIcon} />
                    </span>
                    <span className={classes.chainLabel}>
                      For each record in Step {index}, execute Step {index + 1}
                    </span>
                  </div>
                )}

                {/* Delete sits above the card (right), away from Test/expand, with a
                    text label so it isn't clicked by accident. The first step is the
                    chain root and has no delete control. */}
                {index > 0 && (
                  <div className={classes.stepActions}>
                    <Tooltip title={`Delete Step ${index + 1}`} placement="top" arrow>
                      <button
                        type="button"
                        className={classes.stepDeleteAction}
                        data-testid={`remove-step-${index}`}
                        aria-label={`Delete Step ${index + 1}`}
                        onClick={() => {
                          onRemoveStep(index);
                          setExpandedIndex(Math.max(0, index - 1));
                        }}
                      >
                        <DeleteIcon width={14} height={14} />
                        Delete
                      </button>
                    </Tooltip>
                  </div>
                )}

                <div
                  className={classnames(classes.stepStack, {
                    [classes.stepStackPaginated]: paginated,
                  })}
                >
                  <section
                    id={iteration?.key}
                    data-testid={iteration?.key}
                    className={classnames(classes.stepCard, {
                      [classes.stepCardExpanded]: expanded,
                      [classes.stepCardPaginated]: paginated,
                    })}
                  >
                    <header
                      className={classes.stepHeader}
                      data-testid={`rest-redesign-step-header-${index}`}
                      /* Click anywhere on the header to expand/focus this step (the
                         chevron/badge still toggle — they stopPropagation). */
                      onClick={() => setExpandedIndex(index)}
                    >
                      <button
                        type="button"
                        className={classes.stepBadge}
                        data-testid="iteration-step"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(index);
                        }}
                        aria-label={expanded ? 'Collapse step details' : 'Expand step details'}
                      >
                        {index + 1}
                      </button>

                      <StepUrlComposer
                        stageIndex={index}
                        stageConfig={apiConfig['rest.iterations'][index]}
                        apiURLMacros={apiURLMacros}
                        parentDataSet={parentDataSet}
                        parentDataSetSamples={parentDataSetSamples}
                        editedSamples={editedSamples}
                        methodOptions={methodOptions}
                        dateFormatOptions={dateFormatOptions}
                        timeUnitOptions={timeUnitOptions}
                        credentialData={credentialData}
                        isLoadingProbeSample={isLoadingProbeSample}
                        onAPIConfigChanged={onAPIConfigChanged}
                        onGetParameterizedURLs={onGetParameterizedURLs}
                        onCallAPI={onCallAPI}
                      />

                      <button
                        type="button"
                        className={classes.expandBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(index);
                        }}
                        aria-label={expanded ? 'Collapse step details' : 'Expand step details'}
                        aria-expanded={expanded}
                        data-testid={`expand-step-${index}`}
                      >
                        <Expand
                          className={classnames(classes.expandIcon, {
                            [classes.expandOpen]: expanded,
                          })}
                        />
                      </button>
                    </header>

                    {expanded && (
                      <div className={classes.stepBody}>
                        <ConfigureAPIStage
                          isFocused
                          stageIndex={index}
                          apiConfig={apiConfig}
                          apiURLMacros={apiURLMacros}
                          macrosUpdateTime={macrosUpdateTime}
                          activePanelKey={0}
                          stageAPIConfig={apiConfig['rest.iterations'][index]}
                          apiResponse={apiResponseByStage[index]}
                          parentDataSet={parentDataSet}
                          parentDataSetSamples={parentDataSetSamples}
                          editedSamples={editedSamples}
                          isLoadingProbeSample={isLoadingProbeSample}
                          isLoadingDataMaps={isLoadingDataMaps}
                          dataPathValidationResult={dataPathValidationResultByStage[index]}
                          selectedConnection={selectedConnection}
                          paging={paging}
                          dataMapSamples={dataMapSamples}
                          dataMaps={dataMaps}
                          validateDataPath={validateDataPath}
                          setFormError={setFormError}
                          onLoadDataMaps={loadDataMaps}
                          onCallAPI={onCallAPI}
                          onAPIConfigChanged={onAPIConfigChanged}
                          onGetParameterizedURLs={onGetParameterizedURLs}
                          isAPIServerTarget={isAPIServerTarget}
                          hidePaginationSection
                          hideResponsePathSection
                          hideRequestHeadersSection
                          hideUrlSection
                          hideUrlMacrosSection
                          hideUrlPreview
                          hideResponseViewer
                          hideBodySection
                          redesignSkin
                        />
                        <ResponsePanel
                          stageIndex={index}
                          stageConfig={apiConfig['rest.iterations'][index]}
                          apiResponse={apiResponseByStage[index]}
                          onChange={(name, value) => onAPIConfigChanged(name, value, index)}
                          showBody={['POST', 'PUT', 'PATCH'].includes(
                            apiConfig['rest.iterations'][index]?.method,
                          )}
                          onFieldFocus={setPreviewTab}
                          bodyContent={
                            <RequestBodyEditor
                              stageIndex={index}
                              stageConfig={apiConfig['rest.iterations'][index]}
                              apiURLMacros={apiURLMacros}
                              parentDataSet={parentDataSet}
                              parentDataSetSamples={parentDataSetSamples}
                              editedSamples={editedSamples}
                              dateFormatOptions={dateFormatOptions}
                              timeUnitOptions={timeUnitOptions}
                              onAPIConfigChanged={onAPIConfigChanged}
                            />
                          }
                        />
                      </div>
                    )}
                  </section>

                  {/* Paginate pill hangs OUTSIDE the card, bottom-right (prototype). */}
                  <div className={classes.stepPaginate}>
                    <StepPaginationControl
                      stageIndex={index}
                      stageConfig={apiConfig['rest.iterations'][index]}
                      apiResponse={apiResponseByStage[index]}
                      onChange={(name, value) => onAPIConfigChanged(name, value, index)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <div className={classes.chainConnector} aria-hidden="true">
            <span className={classes.chainArrow}>
              <Expand className={classes.chainArrowIcon} />
            </span>
            <span className={classes.chainLabel}>
              For each record in Step {iterations.length}, execute Step {iterations.length + 1}
            </span>
          </div>

          <button
            type="button"
            className={classes.addStep}
            data-testid="add-another-step"
            onClick={() => onAddStep(() => setExpandedIndex(iterations.length))}
          >
            <AddIcon className={classes.addStepIcon} />
            Add Step
          </button>
        </div>
        {/* Classed wrapper so the stacked narrow-viewport layout can order the
            runtime/schedule cards below the preview (see .belowSteps in scss). */}
        <div className={classes.belowSteps}>{belowSteps}</div>
      </div>

      <div className={classes.previewColumn}>
        <ResponsePreviewPanel
          apiResponse={apiResponseByStage[activeIndex]}
          stageConfig={apiConfig['rest.iterations'][activeIndex] || {}}
          onChange={(name, value) => onAPIConfigChanged(name, value, activeIndex)}
          activeTab={previewTab}
          onTabChange={setPreviewTab}
        />
      </div>
    </div>
  );
};

export default RestApiStepList;
